import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard, Star, Calculator, Wrench, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/AuthContext";
import logo from "@/assets/first-deal-logo.png";
import AuthModal from "@/features/auth/AuthModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CALCULATOR_ITEMS = [
  // Active calculators (Calculator icon)
  { label: "Rental Property Calculator", description: "Pure Cash Flow analysis for buy-and-hold rentals", href: "/tools/rental-analyzer", icon: Calculator },
  { label: "Short-Term Rental Analyzer", description: "Airbnb and vacation rental profitability projections", href: "/tools/str-calculator", icon: Calculator },
  { label: "Mortgage Calculator", description: "Estimate payments, view amortization & borrowing costs", href: "/tools/mortgage-calculator", icon: Calculator },
  { label: "Cash Flow Calculator", description: "Track personal income, expenses & savings monthly", href: "/cashflow-calculator", icon: Calculator },
  // Active tools (Wrench icon)
  { label: "Market Finder Tool", description: "Compare rents vs. home values across every U.S. county", href: "/marketfinder", icon: Wrench },
  { label: "Strategy Finder Quiz", description: "Find your ideal real estate investment strategy", href: "/strategy-finder", icon: Wrench },
  // Coming soon
  { label: "BRRRR Calculator", description: "Buy, Rehab, Rent, Refinance, Repeat cycle analysis", comingSoon: true, icon: Calculator },
  { label: "House Flipping", description: "Fix-and-flip profit calculator", comingSoon: true, icon: Calculator },
  { label: "Wholesaling", description: "Assignment fee and MAO calculator", comingSoon: true, icon: Calculator },
  { label: "Rent by the Room", description: "Maximize cash flow with room rentals", comingSoon: true, icon: Calculator },
  { label: "Mid Term Rental", description: "30+ day furnished rental analysis", comingSoon: true, icon: Calculator },
  { label: "Large Multifamily", description: "Commercial apartment analysis", comingSoon: true, icon: Calculator },
  { label: "Residential Assisted Living", description: "RAL home investment calculator", comingSoon: true, icon: Calculator },
];

const PublicNav = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { session, logout } = useAuth();
  
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<"signup" | "login">("signup");
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openDropdown = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setToolsOpen(true);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setToolsOpen(false), 120);
  };

  const displayName = session?.user?.user_metadata?.name || session?.user?.email?.split('@')[0] || 'User';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border/40 bg-card/80 backdrop-blur-md">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img src={logo} alt="First Deal" className="h-20 w-auto" />
        </Link>

        {/* Desktop links */}
        <div className="hidden items-center gap-6 md:flex">
          {/* Home link */}
          <Link
            to="/"
            className={`text-sm font-medium transition-colors hover:text-primary ${
              pathname === "/" ? "text-primary" : "text-foreground/70"
            }`}
          >
            Home
          </Link>

          {/* Calculators dropdown for non-authenticated only */}
          {!session && (
            <div className="relative" ref={toolsRef} onMouseEnter={openDropdown} onMouseLeave={scheduleClose}>
              <div className="flex items-center gap-0.5">
                <Link
                  to="/tools"
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    pathname.startsWith("/tools") ? "text-primary" : "text-foreground/70"
                  }`}
                >
                  Free Tools & Calculators
                </Link>
                <span className={`p-1 transition-colors ${pathname.startsWith("/tools") ? "text-primary" : "text-foreground/70"}`}>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${toolsOpen ? "rotate-180" : ""}`} />
                </span>
              </div>
              {toolsOpen && (
                <div
                  className="absolute left-0 top-full z-50 pt-1"
                  onMouseEnter={openDropdown}
                  onMouseLeave={scheduleClose}
                >
                  <div className="w-80 bg-card border border-border rounded-lg shadow-lg max-h-[70vh] overflow-y-auto py-1">
                    {CALCULATOR_ITEMS.map((item, i) => (
                      item.href && !item.comingSoon ? (
                        <Link key={i} to={item.href} onClick={() => setToolsOpen(false)} className="flex items-start gap-3 p-3 hover:bg-accent transition-colors">
                          <item.icon className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-foreground text-sm">{item.label}</span>
                            <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                          </div>
                        </Link>
                      ) : (
                        <div key={i} className="flex items-start gap-3 p-3 opacity-60">
                          <item.icon className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-foreground text-sm">{item.label}</span>
                              <span className="text-[10px] font-medium text-muted-foreground border border-border rounded px-1.5 py-0.5">Coming Soon</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Auth section */}
          {/* Dashboard gold button - only when logged in */}
          {session && (
            <Button asChild className="gradient-gold shadow-gold text-white" size="sm">
              <Link to="/dashboard" className="flex items-center gap-1.5">
                <LayoutDashboard className="h-3.5 w-3.5" />
                Dashboard
              </Link>
            </Button>
          )}

          {session ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors min-h-[44px]">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  {displayName}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="text-xs text-muted-foreground" disabled>
                  {session.user.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/saved-deals')} className="cursor-pointer">
                  <Star className="h-4 w-4 mr-2" />
                  Saved Deals
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <button onClick={() => { setAuthModalMode("login"); setShowAuthModal(true); }} className="text-sm font-medium text-foreground/70 hover:text-foreground">
                Log In
              </button>
              <Button asChild className="gradient-gold shadow-gold text-white">
                <a href="https://join.firstdeal.com/main-calendar-app-form/" target="_blank" rel="noopener noreferrer">Apply to First Deal</a>
              </Button>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button className="md:hidden p-2 -mr-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-border bg-card px-4 py-4 md:hidden">
          <div className="flex flex-col gap-4">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="text-sm font-medium text-foreground/70 hover:text-primary"
            >
              Home
            </Link>
            {!session && (
              <Link
                to="/tools"
                onClick={() => setMobileOpen(false)}
                className="text-sm font-medium text-foreground/70 hover:text-primary"
              >
                Free Tools & Calculators
              </Link>
            )}

            {/* Dashboard gold button - only when logged in */}
            {session && (
              <div className="border-t border-border pt-3">
                <Button asChild className="gradient-gold shadow-gold text-white w-full" size="sm">
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2"
                  >
                    <LayoutDashboard className="h-3.5 w-3.5" />
                    Dashboard
                  </Link>
                </Button>
              </div>
            )}

            {/* Auth */}
            <div className="border-t border-border pt-3">
              {session ? (
                <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="text-sm font-medium text-foreground/70 flex items-center gap-2">
                  <LogOut className="h-4 w-4" /> Sign Out
                </button>
              ) : (
                <div className="flex flex-col gap-2">
                  <Button asChild className="gradient-gold shadow-gold text-white w-full">
                    <a href="https://join.firstdeal.com/main-calendar-app-form/" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)}>Apply to First Deal</a>
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => { setAuthModalMode("login"); setShowAuthModal(true); setMobileOpen(false); }}>
                    Login
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={setShowAuthModal} mode={authModalMode} />
    </header>
  );
};

export default PublicNav;
